/*
 * Component for viewing subscription order history and order lines
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, InputLabel, Paper, Tooltip, Typography,
         TableContainer, Table,  TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';

import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CheckCircle from '@mui/icons-material/CheckCircle';

import SubscriptionTableRow from '../suborder/SubscriptionTableRow';
import ServiceTableRow from '../suborder/ServiceTableRow';
import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import SavingSpinner from '../ui/SavingSpinner';
import { getDayLabel } from '../../utils/InputUtils';

import { grey, green } from '@mui/material/colors';
import { format } from 'date-fns';
import { ArrowBack, ArrowForward, HelpOutline } from '@mui/icons-material';
//import AppBindStyles from '../../AppBindStyles';

const styleSheet = theme => ({
    root: {
      width: '100%',
    },
    appnameitem: {
      marginLeft: 8,
    },
    paper: {
        textAlign: 'left',
        maxWidth: 1200,
        marginTop: 16,
        padding: theme.spacing(2)
    },
    paperInner: {
      padding: theme.spacing(2)
    },
    divider: {
      backgroundColor: 'black',
    },
    tblHead: {
      backgroundColor: '#f1f1f1',
    },
    tblHeadRow: {
      height: 24,
    },
    tblHeadCell: {
      textAlign: 'left',
      verticalAlign: 'bottom',
    },
    tblHeadCellB: {
      textAlign: 'right',
      verticalAlign: 'bottom'
    },
    tblWidth: {
      maxWidth: 400,
    },
    dateBlockWidth: {
      maxWidth: 450,
    },
    dateLabelWidth: {
      minWidth: 100
    }
});

const abEnvironment = new AbEnvironment();

//data not used: appId
//removed using: billingDayOfMonth, line.(description, appSkuPrice), 
const SubscriptionOrderViewQuery = graphql`
query SubscriptionOrderViewQuery($orderId: UUID!) {
  orderTByOrderId(orderId: $orderId) {
		orderId
    orderNumber
    orderStatus
    sentTime
    acceptedTime
    inactiveTime
    previousOrder
    replacingOrder
    maxMonthlyBudget
    payFromBalance
    useBudgets
    autoTopupActive
    initialBalanceAmount
    autoTopupAmount
    autoTopupThreshold
    preApproveSubs
    billingDayOfMonth
    approvedBy
    userTByApprovedBy {
      firstName
      lastName
    }
    approvedByEmail
    approvedByFirstName
    approvedByLastName
    sellerAccountName
    sellerLastName
    sellerFirstName
    sellerEmail
    sellerPhone
    sellerAddress1
    sellerAddress2
    sellerCity
    sellerState
    sellerCountry
    sellerPostalCode
    accountTBySellerId {
      accountName
      contactEmail
      contactFirstName
      contactLastName
      logo
      address1
      address2
      city
      state
      country
      postalCode
    }
    customerId
    customerAccountName
    customerLastName
    customerFirstName
    customerEmail
    customerPhone
    customerAddress1
    customerAddress2
    customerCity
    customerState
    customerCountry
    customerPostalCode
    accountTByCustomerId {
      accountName
      contactEmail
      contactFirstName
      contactLastName
      address1
      address2
      city
      state
      country
      postalCode
    }
    orderLineTsByOrderId (orderBy: APP_NAME_ASC) {
      totalCount
      nodes {
        orderLineId
        lineType
        lineDescription
        appSku
        appName
        appSkuPrice
        passThroughFees
        billingFrequency
        billingDayOfMonth
        paymentOrder
        customerAppId
        appMarkupPercentage
        paymentOrder
        appTByAppId {
          appId
          appLogo
          appName
        }
      }
    }
  }
}
`;

const StyledTable = styled(Table) (({ theme }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  //borderColor: theme.palette.colors.secondary[200],
  borderColor: theme.palette.divider,
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.common.black,
    // color: theme.palette.common.white,
    // backgroundColor: theme.palette.colors.secondary[200],
    backgroundColor: theme.palette.divider,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  /*
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  */
  height: 50, // Make the row heights consistent.  Smaller than this could be better, but then padding needs to be adjusted.
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));


class SubscriptionOrderView extends Component {
    constructor(props) {
      super(props);

      this.state = {
        downloadClicked:  false,
        imgHeightOrWidth: 'height'
      };  
      this.onImgLoad = this.onImgLoad.bind(this);
    }

    componentDidMount() {
      if (abEnvironment.isSegmentActive()) { window.analytics.page(); }
    }
  
    onImgLoad({target:img}) {
      if (img.offsetHeight > img.offsetWidth) {
        if (this.state.imgHeightOrWidth !== 'height') {
          this.setState({imgHeightOrWidth: 'height'});
        }
      } else if (img.offsetHeight < img.offsetWidth) {
        if (this.state.imgHeightOrWidth !== 'width') {
          this.setState({imgHeightOrWidth: 'width'});
        }
      } 
    }
  
    getValueForRender(state_member, prop_member, theDefault)
    {
      if (state_member != null)
        return state_member;
  
      if (prop_member != null)
        return prop_member;
  
      return theDefault;
    }

    orderLineTypeDisplay(lineType) {
      if (lineType === 1) 
        return 'Service';
      if (lineType === 0)
        return 'Subscription';
      return '';
    }

    goTo(url) {
      this.props.history.push(`${url}`);
    }

    downloadPdf(orderId) { 
      this.setState({downloadClicked: true});
      const { callPartnerApi } = this.props.auth;
  
      Promise.resolve()
      .then(() => {
  
        let aPjwt = abEnvironment.getPjwt();
        let payload = {
          operation: "generateSOPDF",
          params: {
            pJwt: aPjwt,
            orderId: orderId
          }
        };
    
        return callPartnerApi('appbind/generic', 'POST', payload);
      })
      .then(response => {
        return response.json();
      })
      .then(jresp => {
        if (jresp.status === 0) {
          let pdfString = jresp.pdfString;
          let SONumber = jresp.SONumber;
          let downloadName = '' + SONumber + '.pdf';
    
          var hiddenElement = document.createElement('a');
          hiddenElement.href = 'data:application/pdf;base64,' + pdfString;
          hiddenElement.target = '_blank';
          hiddenElement.download = downloadName;
          hiddenElement.click();
          this.setState({downloadClicked: false});
        } else {
          alert("Error: " + jresp.message);
          this.setState({downloadClicked: false});
        }
  
      })
      .catch(err => {
        /*let sbmesg = '';
        if (err.message != null) {
          sbmesg = 'Error in invoice lookup: ' + err.message;
        } else {
          sbmesg = 'Unidentified error in invoice lookup';
        }*/
        this.setState({downloadClicked: false});
      });
    }
  
    getBillingDayLabel(billingDayOfMonth) {

      if (billingDayOfMonth === 32) { 
        return '(billed on the last day of month)'; 
      } 
    
      return '(billed on the ' + getDayLabel(billingDayOfMonth) + ' day of the month)';
    }
  
    render() {
      const { isAuthenticated, isUnauthenticatedCustomer, isAuthenticatedCustomer, isAuthenticatedReseller, needAssent, logError } = this.props.auth;
      const classes = this.props.classes;
      const statusReplaced = grey[500];
      const statusOk = green[300];
  
      if (!isAuthenticated() || needAssent()) {
        return (
          <Redirect to='/home' />
        );
      } else if (isAuthenticatedCustomer() || isUnauthenticatedCustomer() || isAuthenticatedReseller() ) {
        const params = new URLSearchParams(this.props.location.search);
        let orderId = params.get('orderId');
  
        let fp = params.get('fp');
        let backTo = "Subscriptions";
        if (fp != null && fp !== '') {
          backTo = fp;
        }
  
        return (
          <QueryRenderer
            environment={abEnvironment.getEnvironment()}
            query={SubscriptionOrderViewQuery}
            variables={{orderId: orderId}}
            render={({error, props})=> {
              if (error) {
                logError(error.message);
                return (
                  <ServerError/>
                );
              } else if (props) {
                //If no order retrieved from db, return not found and go back.
                if (props.orderTByOrderId === null) {
                  return (
                    <PageTitle title={"Subscription not found"} showBackButton={true} name={backTo} history={this.props.history}/>
                  );
                }

                let theSubscriptionOrder = props.orderTByOrderId;
                //order status: (only 2 and 3 are used currently.) 0 - Draft,  1 - Pending; 2 - Active; 3 - Archived';
                let orderStatus = theSubscriptionOrder.orderStatus;

                // Reseller information
                let reseller = theSubscriptionOrder.accountTBySellerId;
                
                let resellerAccountName = theSubscriptionOrder.sellerAccountName;
                if (resellerAccountName == null || resellerAccountName.length === 0) {
                  resellerAccountName = reseller.accountName;
                }
                let resellerContactFirstName = theSubscriptionOrder.sellerFirstName;
                if (resellerContactFirstName == null || resellerContactFirstName.length === 0) {
                  resellerContactFirstName = reseller.contactFirstName;
                }
                let resellerContactLastName = theSubscriptionOrder.sellerLastName;
                if (resellerContactLastName == null || resellerContactLastName.length === 0) {
                  resellerContactLastName = reseller.contactLastName;
                }
                let resellerContactEmail = theSubscriptionOrder.sellerEmail;
                if (resellerContactEmail == null || resellerContactEmail.length === 0) {
                  resellerContactEmail = reseller.contactEmail;
                }
                let resellerAddress1 = theSubscriptionOrder.sellerAddress1;
                if (resellerAddress1 == null || resellerAddress1.length === 0) {
                  resellerAddress1 = reseller.address1;
                }
                let resellerAddress2 = theSubscriptionOrder.sellerAddress2;
                if (resellerAddress2 == null || resellerAddress2.length === 0) {
                  resellerAddress2 = reseller.address2;
                }
                let resellerCity = theSubscriptionOrder.sellerCity;
                if (resellerCity == null || resellerCity.length === 0) {
                  resellerCity = reseller.city;
                }
                let resellerState = theSubscriptionOrder.sellerState;
                if (resellerState == null || resellerState.length === 0) {
                  resellerState = reseller.state;
                }
                let resellerPostalCode = theSubscriptionOrder.sellerPostalCode;
                if (resellerPostalCode == null || resellerPostalCode.length === 0) {
                  resellerPostalCode = reseller.postalCode;
                }

                let rLine1  = resellerAddress1;
                let rLine1a = resellerAddress2;
                if (rLine1a != null && rLine1a !== '') {
                    rLine1 = rLine1 + ', ' + rLine1a;
                }
                let rLine2 = resellerCity + ', ' + resellerState + ' ' + resellerPostalCode;

                // Customer information
                let customer = theSubscriptionOrder.accountTByCustomerId;

                let customerAccountName = theSubscriptionOrder.customerAccountName;
                if (customerAccountName == null || customerAccountName.length === 0) {
                  customerAccountName = customer.accountName;
                }
                let customerContactFirstName = theSubscriptionOrder.customerFirstName;
                if (customerContactFirstName == null || customerContactFirstName.length === 0) {
                  customerContactFirstName = customer.contactFirstName;
                }
                let customerContactLastName = theSubscriptionOrder.customerLastName;
                if (customerContactLastName == null || customerContactLastName.length === 0) {
                  customerContactLastName = customer.contactLastName;
                }
                let customerContactEmail = theSubscriptionOrder.customerEmail;
                if (customerContactEmail == null || customerContactEmail.length === 0) {
                  customerContactEmail = customer.contactEmail;
                }
                let customerAddress1 = theSubscriptionOrder.customerAddress1;
                if (customerAddress1 == null || customerAddress1.length === 0) {
                  customerAddress1 = customer.address1;
                }
                let customerAddress2 = theSubscriptionOrder.customerAddress2;
                if (customerAddress2 == null || customerAddress2.length === 0) {
                  customerAddress2 = customer.address2;
                }
                let customerCity = theSubscriptionOrder.customerCity;
                if (customerCity == null || customerCity.length === 0) {
                  customerCity = customer.city;
                }
                let customerState = theSubscriptionOrder.customerState;
                if (customerState == null || customerState.length === 0) {
                  customerState = customer.state;
                }
                let customerPostalCode = theSubscriptionOrder.customerPostalCode;
                if (customerPostalCode == null || customerPostalCode.length === 0) {
                  customerPostalCode = customer.postalCode;
                }

                let cLine1  = customerAddress1;
                let cLine1a = customerAddress2;
                if (cLine1a != null && cLine1a !== '') {
                    cLine1 = cLine1 + ', ' + cLine1a;
                }
                let cLine2 = customerCity + ', ' + customerState + ' ' + customerPostalCode;
               
                let backLink = 'myapps';
                if (isAuthenticatedReseller()) {
                  //subscription orders is on customer page in reseller view
                  backLink = "customer?accountId=" + theSubscriptionOrder.customerId;
                }
  
                let sentTimeDisplay;
                if (theSubscriptionOrder.sentTime) {
                  //get as date by appending 'Z', since this has no timezone.
                  let sentTimeDate = new Date(theSubscriptionOrder.sentTime+'Z');
                  sentTimeDisplay = format(sentTimeDate, 'PP @ pp');
                } 

                let orderAcceptedDisplay;
                if (theSubscriptionOrder.acceptedTime) {
                  //get as date by appending 'Z', since this has no timezone.
                  let orderAcceptedDate = new Date(theSubscriptionOrder.acceptedTime+'Z');
                  orderAcceptedDisplay = format(orderAcceptedDate, 'PP @ pp');
                } else {
                  //doesn't look like this happens currently. Will eventually have draft or pending orders.
                  orderAcceptedDisplay = 'Order has not been accepted by customer';
                }
                
                // Handle getting the approved by information.
                let approvedByFirstName = theSubscriptionOrder.approvedByFirstName;
                if (approvedByFirstName == null || approvedByFirstName.length === 0) {
                  if (theSubscriptionOrder.userTByApprovedBy != null) {
                    approvedByFirstName = theSubscriptionOrder.userTByApprovedBy.firstName;
                  }
                }
                let approvedByLastName  = theSubscriptionOrder.approvedByLastName;
                if (approvedByLastName == null || approvedByLastName.length === 0) {
                  if (theSubscriptionOrder.userTByApprovedBy != null) {
                    approvedByLastName = theSubscriptionOrder.userTByApprovedBy.lastName;
                  }
                }

                let approvedBy;                
                if (approvedByFirstName != null && approvedByFirstName.length > 0 && approvedByLastName != null && approvedByLastName.length > 0) {
                  approvedBy = approvedByFirstName + ' ' + approvedByLastName + ' (' + theSubscriptionOrder.approvedByEmail + ')'; 
                } else {
                  approvedBy = theSubscriptionOrder.approvedByEmail;
                }

                // Order information
                let previousOrderId = theSubscriptionOrder.previousOrder;
                let replacingOrderId = theSubscriptionOrder.replacingOrder ? theSubscriptionOrder.replacingOrder : null;
                
                let orderLines = null;
                if (theSubscriptionOrder.orderLineTsByOrderId) {
                  orderLines = theSubscriptionOrder.orderLineTsByOrderId.nodes;
                }

                let paidSubscriptionCount = 0;
                let subscriptions = [];
                let services = [];
                let i = 0;
                for (i = 0; i < orderLines.length; i++) {
                  let line = orderLines[i];
                  if (line.lineType === 0) {
                    subscriptions.push(line);
                    if (line.paymentOrder === 0 || line.paymentOrder === 1) {
                      paidSubscriptionCount++;
                    }
                  } else {
                    services.push(line);
                  }
                }

                let pTitle = 'Subscription order for ' + customer.accountName;
                let pSubtitle = 'Detailed view of subscription history';

                return (
                  <Grid container spacing={3} direction="row" justifyContent="flex-start">
                    <Grid item xs={12}>
                      <PageTitle title={pTitle} subtitle={pSubtitle} showBackButton={true} name={backTo} backLink={backLink} noMargin={true} history={this.props.history}/>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper className={classes.paper} >
                        {/* page title segment: title and buttons */}
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Grid container spacing={1} justifyContent="space-between">
                              <Grid item>
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item>
                                    <Typography variant="h5">Subscription order {theSubscriptionOrder.orderNumber}</Typography>
                                  </Grid>
                                  <Grid item>
                                    { orderStatus === 2 && 
                                    <Tooltip placement='right' title='Subscription order is currently active.'>
                                      <CheckCircle htmlColor={statusOk} style={{verticalAlign: "-10%"}}/>
                                    </Tooltip>}
                                    { orderStatus === 3 && 
                                    <Tooltip placement='right' title='This is an archive of an old subscription order.'>
                                      <CheckCircleOutline htmlColor={statusReplaced}/>
                                    </Tooltip>}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item paddingBottom={1}>
                                <Grid container spacing={1}>
                                  { previousOrderId && 
                                  <Grid item>
                                    <Button
                                      color="primary"
                                      variant="contained"
                                      disabled={!previousOrderId}
                                      onClick={() => {this.props.history.push(`subscriptionorderview?orderId=`+previousOrderId);}}
                                      >
                                      <ArrowBack/>Previous Order
                                    </Button>
                                  </Grid>}
                                  { replacingOrderId &&
                                  <Grid item>
                                    <Button 
                                      color="primary"
                                      variant="contained"
                                      disabled={!replacingOrderId}
                                      onClick={() => {this.props.history.push(`subscriptionorderview?orderId=`+replacingOrderId);}}
                                    >
                                      <ArrowForward/>Replacing Order
                                    </Button>
                                  </Grid>}
                                  { !this.state.downloadClicked &&
                                  <Grid item>
                                    <Button
                                      color="primary"
                                      variant="outlined"
                                      onClick={() => { this.downloadPdf(orderId)}}
                                    >
                                      Download
                                    </Button>
                                  </Grid>}
                                  { this.state.downloadClicked &&
                                  <Grid item > 
                                    <Button color="primary" variant="outlined" >
                                      <SavingSpinner/>
                                    </Button>
                                  </Grid>}
                                </Grid>
                              </Grid>

                              <Grid item sm={12}>
                                <Grid container className={classes.dateBlockWidth} justifyContent="space-between" alignItems="center">
                                  <Grid item className={classes.dateLabelWidth}>
                                    {theSubscriptionOrder.orderNumber &&
                                    <Typography variant="body1">
                                      SO number
                                    </Typography>}
                                    {sentTimeDisplay &&
                                    <Typography variant="body1">Sent date</Typography>}
                                    {approvedBy &&
                                    <Typography variant="body1">Approved by</Typography>}
                                    <Typography variant="body1">Accepted date</Typography>
                                    {theSubscriptionOrder.inactiveTime &&
                                    <Typography variant="body1">Inactive date</Typography>}
                                  </Grid>

                                  <Grid item className={classes.dateLabelWidth}>
                                    {theSubscriptionOrder.orderNumber &&
                                    <Typography variant="body1"><strong>{ theSubscriptionOrder.orderNumber}</strong></Typography>}
                                    {sentTimeDisplay &&
                                    <Typography variant="body1">{ sentTimeDisplay }</Typography>}
                                    {approvedBy &&
                                    <Typography variant="body1">{ approvedBy }</Typography>}
                                    <Typography variant="body1">{ orderAcceptedDisplay }</Typography>
                                    {theSubscriptionOrder.inactiveTime &&
                                    <Typography variant="body1">{ format(new Date(theSubscriptionOrder.inactiveTime+'Z'), 'PP @ pp') }</Typography>}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item xs={12}>
                            <Paper className={classes.paperInner}>
                              <Grid container spacing={3}>
                                { reseller.logo != null &&
                                <Grid item>
                                  <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                      <InputLabel shrink>Agent</InputLabel>
                                    </Grid>
                                    <Grid item>
                                      <Grid container spacing={2}>
                                        <Grid item>
                                          {this.state.imgHeightOrWidth === 'width' &&
                                          <img alt="Logo" width="52px" onLoad={this.onImgLoad} src={reseller.logo}/>}
                                          {this.state.imgHeightOrWidth === 'height' &&
                                          <img alt="Logo" height="52px" onLoad={this.onImgLoad} src={reseller.logo}/>}
                                        </Grid>
                                        <Grid item className={classes.tblWidth}>
                                          <Typography variant="body2"><strong>{resellerAccountName}</strong></Typography>
                                          <Typography variant="body2">{resellerContactFirstName} {resellerContactLastName}</Typography>
                                          <Typography variant="body2">{resellerContactEmail}</Typography>
                                          <Typography variant="body2">{rLine1}</Typography>
                                          <Typography variant="body2">{rLine2}</Typography>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>}
                                { reseller.logo == null &&
                                <Grid item className={classes.tblWidth}>
                                  <InputLabel shrink>Agent</InputLabel>
                                  <Typography variant="body2"><strong>{resellerAccountName}</strong></Typography>
                                  <Typography variant="body2">{resellerContactFirstName} {resellerContactLastName}</Typography>
                                  <Typography variant="body2">{resellerContactEmail}</Typography>
                                  <Typography variant="body2">{rLine1}</Typography>
                                  <Typography variant="body2">{rLine2}</Typography>
                                </Grid>}
                                <Grid item className={classes.tblWidth}>
                                  <InputLabel shrink>Customer</InputLabel>
                                  <Typography variant="body2"><strong>{customerAccountName}</strong></Typography>
                                  <Typography variant="body2">{customerContactFirstName} {customerContactLastName}</Typography>
                                  <Typography variant="body2">{customerContactEmail}</Typography>
                                  <Typography variant="body2">{cLine1}</Typography>
                                  <Typography variant="body2">{cLine2}</Typography>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>

                          {/* Lower segment; table for order lines. */}
                          {subscriptions.length > 0 &&
                          <Grid item xs={12}>
                            <Paper className={classes.paperInner}>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                      <Typography align="left" variant="h6">Subscriptions</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                      <Typography align="left" variant="body2">We're managing these subscriptions for your project.</Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <TableContainer>
                                    <StyledTable>
                                      <TableHead>
                                        <TableRow>
                                          <StyledTableCell sx={{ borderTopLeftRadius: 8}}/>
                                          <StyledTableCell style={{minWidth: 152}}>App</StyledTableCell>
                                          <StyledTableCell>Sharing model</StyledTableCell>
                                          <StyledTableCell sx={{ borderTopRightRadius: 8}}/>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {subscriptions.map(n => {
                                          return (
                                            <SubscriptionTableRow 
                                              key={n.customerAppId}
                                              soContext="orderHistory"
                                              subscription={n}
                                              styledTableRow={StyledTableRow}
                                              styledTableCell={StyledTableCell}
                                              editable={false}
                                              showModel={true}
                                            />
                                          );
                                        })}
                                      </TableBody>
                                    </StyledTable>
                                  </TableContainer>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>}

                          {services.length > 0 &&
                          <Grid item xs={12}>
                            <Paper className={classes.paperInner}>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                      <Typography align="left" variant="h6">Services</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                      <Typography align="left" variant="body2">We're providing these services for your project.</Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                  <TableContainer>
                                    <StyledTable>
                                      <TableHead>
                                        <TableRow>
                                          <StyledTableCell sx={{ borderTopLeftRadius: 8}}/>
                                          <StyledTableCell>Service</StyledTableCell>
                                          <StyledTableCell>Amount</StyledTableCell>
                                          <StyledTableCell>Billing freqency</StyledTableCell>
                                          <StyledTableCell>Billing day</StyledTableCell>
                                          <StyledTableCell sx={{ borderTopRightRadius: 8}}/>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {services.map(n => {
                                          return (
                                            <ServiceTableRow 
                                              key={n.customerAppId}
                                              service={n}
                                              styledTableRow={StyledTableRow}
                                              styledTableCell={StyledTableCell}
                                              editable={false}
                                              showModel={true}
                                            />
                                          );
                                        })}
                                      </TableBody>
                                    </StyledTable>
                                  </TableContainer>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>}

                          {/* Summary segment: expense wallet.  Only show if there are wallet settings to show */}
                          {(paidSubscriptionCount > 0 || theSubscriptionOrder.initialBalanceAmount > 0 || theSubscriptionOrder.autoTopupThreshold > 0 || theSubscriptionOrder.autoTopupAmount > 0) &&
                          <Grid item xs={12}>
                            <Paper className={classes.paperInner}>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                      <Typography align="left" variant="h6">Expense wallet</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                      <Typography align="left" variant="body2">Your expense wallet is used to fund subscriptions for your project.</Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <Grid container spacing={0}>
                                    {!previousOrderId && theSubscriptionOrder.initialBalanceAmount > 0 &&
                                    <Grid item xs={12}>
                                      <Grid container className={classes.tblWidth} justifyContent="space-between" alignItems="center">
                                        <Grid item xs={7}>
                                          <Typography variant="body1">Initially fund wallet to:</Typography>
                                        </Grid>
                                        <Grid item xs={4} align="right">
                                          <Typography variant="body1">
                                            {Number(theSubscriptionOrder.initialBalanceAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2})}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={1} paddingLeft={1}>
                                          <Tooltip placement='right' title='Your wallet was funded to this amount when you confirmed this order.'>
                                            <HelpOutline fontSize="small" style={{verticalAlign: "-10%"}}/>
                                          </Tooltip>
                                        </Grid>
                                      </Grid>
                                    </Grid>}
                                    {previousOrderId && theSubscriptionOrder.initialBalanceAmount > 0 &&
                                    <Grid item xs={12}>
                                      <Grid container className={classes.tblWidth} justifyContent="space-between" alignItems="center">
                                        <Grid item xs={7}>
                                          <Typography variant="body1">Add funds now:</Typography>
                                        </Grid>
                                        <Grid item xs={4} align="right">
                                          <Typography variant="body1">
                                            {Number(theSubscriptionOrder.initialBalanceAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2})}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={1} paddingLeft={1}>
                                          <Tooltip placement='right' title='This amount was added to your wallet when you confirmed this order.'>
                                            <HelpOutline fontSize="small" style={{verticalAlign: "-10%"}}/>
                                          </Tooltip>
                                        </Grid>
                                      </Grid>
                                    </Grid>}
                                    {Number(theSubscriptionOrder.autoTopupThreshold) > 0 && Number(theSubscriptionOrder.autoTopupAmount) > 0 &&
                                    <Grid item xs={12}>
                                      <Grid container className={classes.tblWidth} justifyContent="space-between" alignItems="center">
                                        <Grid item xs={7}>
                                          <Typography variant="body1">When your wallet balance falls below:</Typography>
                                        </Grid>
                                        <Grid item xs={4} align="right">
                                          <Typography variant="body1">
                                            {Number(theSubscriptionOrder.autoTopupThreshold).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2})}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={1} paddingLeft={1}>
                                          <Tooltip placement='right' title='When your wallet balance goes below this value, your funding source will be charged to increase your wallet balance to the target amount.'>
                                            <HelpOutline fontSize="small" style={{verticalAlign: "-10%"}}/>
                                          </Tooltip>
                                        </Grid>
                                      </Grid>
                                    </Grid>}
                                    {Number(theSubscriptionOrder.autoTopupThreshold) > 0 && Number(theSubscriptionOrder.autoTopupAmount) > 0 &&
                                    <Grid item xs={12}>
                                      <Grid container className={classes.tblWidth} justifyContent="space-between" alignItems="center">
                                        <Grid item xs={7}>
                                          <Typography variant="body1">Automatically fund balance to:</Typography>
                                        </Grid>
                                        <Grid item xs={4} align="right">
                                          <Typography variant="body1">
                                            {Number(theSubscriptionOrder.autoTopupAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2})}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={1} paddingLeft={1}>
                                          <Tooltip placement='right' title='When your wallet balance needs to be increased, your funding source will be charged to increase your wallet balance to this amount.'>
                                            <HelpOutline fontSize="small" style={{verticalAlign: "-10%"}}/>
                                          </Tooltip>
                                        </Grid>
                                      </Grid>
                                    </Grid>}

                                    <Grid item xs={12}>
                                      {theSubscriptionOrder.maxMonthlyBudget != null &&
                                      <Grid container className={classes.tblWidth} justifyContent="space-between" alignItems="center">
                                        <Grid item xs={7}>
                                          <Typography variant="body1">Limit monthly spend to:</Typography>
                                        </Grid>
                                        <Grid item xs={4} align="right">
                                          <Typography variant="body1">
                                            {Number(theSubscriptionOrder.maxMonthlyBudget).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2})}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={1} paddingLeft={1}>
                                          <Tooltip placement='right' title='This is the maximum amount that can be spent per month for subscription charges.'>
                                            <HelpOutline fontSize="small" style={{verticalAlign: "-10%"}}/>
                                          </Tooltip>
                                        </Grid>
                                      </Grid>}
                                      {theSubscriptionOrder.maxMonthlyBudget == null &&
                                      <Grid container className={classes.tblWidth} justifyContent="space-between" alignItems="center">
                                        <Grid item xs={7}>
                                          <Typography variant="body1">Limit monthly spend to:</Typography>
                                        </Grid>
                                        <Grid item xs={4} align="right">
                                          <Typography variant="body1">
                                            No maximum
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={1} paddingLeft={1}>
                                          <Tooltip placement='right' title='No monthly maximum has been set to limit subscription charges.'>
                                            <HelpOutline fontSize="small" style={{verticalAlign: "-10%"}}/>
                                          </Tooltip>
                                        </Grid>
                                      </Grid>}
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>}
                        </Grid> 
                      </Paper>
                    </Grid>
                  </Grid>
                );
              }
              return <LoadingSpinner />
            }}
          />
        )
      } else {
        return (
          <Redirect to='/home' />
        );
      }
    }
  }

SubscriptionOrderView.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(SubscriptionOrderView);
  